import {
  Box,
  HStack,
  Tab as ChakraTab,
  TabList,
  TabPanel,
  TabPanels,
  TabProps,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { RiSettingsLine, RiToggleLine } from "@remixicon/react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router";

import LinearLogo from "@/common/assets/linear-logo.svg?react";
import ErrorBoundary from "@/common/components/ErrorBoundary";
import {
  IcoAutoFeedbackSurveys,
  IcoSlackMonotone,
  IcoTrackingEvent,
} from "@/common/components/Icons";
import { SettingsFeedback } from "@/feature/pages/settings/SettingsFeedback";
import { SettingsGeneral } from "@/feature/pages/settings/SettingsGeneral";
import { SettingsLinear } from "@/feature/pages/settings/SettingsLinear";
import { SettingsOptIn } from "@/feature/pages/settings/SettingsOptIn";
import { SettingsSlack } from "@/feature/pages/settings/SettingsSlack";
import { SettingsUsage } from "@/feature/pages/settings/SettingsUsage";
import { useFlag } from "@reflag/react-sdk";
import { FeatureSettingsSections } from "@reflag/shared/urls";

function Tab({
  children,
  to,
  ...props
}: { children: React.ReactNode; to: string } & TabProps) {
  return (
    <ChakraTab as={Link} to={to} {...props}>
      {children}
    </ChakraTab>
  );
}

type Route = {
  to: FeatureSettingsSections;
  name: string;
  icon: JSX.Element;
  panel: JSX.Element;
  meta?: Record<string, any>;
};

export default function FeatureSettings() {
  const { section = "general" } = useParams() as {
    section: FeatureSettingsSections;
  };
  const { isEnabled: showEndUserOptInUi } = useFlag("end-user-opt-in-ui");

  const routes: Route[] = [
    {
      name: "General",
      to: "general",
      icon: <RiSettingsLine size={18} />,
      panel: <SettingsGeneral />,
    },
    {
      name: "Tracking",
      to: "adoption",
      icon: <IcoTrackingEvent size="lg" />,
      panel: <SettingsUsage />,
    },
    {
      name: "Feedback",
      to: "feedback",
      icon: <IcoAutoFeedbackSurveys size="lg" />,
      panel: <SettingsFeedback />,
    },
    ...(showEndUserOptInUi
      ? [
          {
            name: "Opt-in",
            to: "opt-in" as const,
            icon: <RiToggleLine size={18} />,
            panel: <SettingsOptIn />,
          },
        ]
      : []),
    {
      name: "Slack",
      to: "slack",
      icon: <IcoSlackMonotone size="lg" />,
      panel: <SettingsSlack />,
    },
    {
      name: "Linear",
      to: "linear" as const,
      icon: <LinearLogo height={16} opacity="1" width={16} />,
      panel: <SettingsLinear />,
    },
  ];

  const selectedSection =
    section === "opt-in" && !showEndUserOptInUi ? "general" : section;
  const sections = routes.map((r) => r.to);

  const currentSectionIndex = sections.indexOf(selectedSection);
  const currentRoute = routes.find((r) => r.to === selectedSection);

  return (
    <>
      <Helmet>
        <title>Flag Settings › {currentRoute?.name ?? ""}</title>
      </Helmet>
      {/*
        This extra wrapper is needed to have Safari not be confused about the height.
        If you put the `flexGrow` on the `Tabs` container, Safari will size Tabs grid-children
        taller than the Tabs grid-container, causing higher than viewport content.
      */}

      <Box display="flex" flexGrow={1}>
        <Tabs
          index={currentSectionIndex}
          orientation="vertical"
          variant="sideMenu"
          w="100%"
          isLazy
          isManual
        >
          <TabList>
            {routes.map((route) => (
              <Tab key={route.to} to={`../${route.to}`}>
                <HStack>
                  {route.icon}
                  <Text>{route.name}</Text>
                </HStack>
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {routes.map((route) => (
              <TabPanel key={route.to}>
                <ErrorBoundary>{route.panel}</ErrorBoundary>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Box>
    </>
  );
}
